import { useEffect, useMemo, useState } from 'react'
import { requestSubscription, useLazyLoadQuery } from 'react-relay'
import { useDispatch } from 'react-redux'
import Env from '../RelayEnvironment'
import { graphql } from 'react-relay'
import { useEliteSeatProgressUpdatedSubscription } from './__generated__/useEliteSeatProgressUpdatedSubscription.graphql'
import { getWagerLeftValueAndBonus } from '@src/pages/Profile/Dashboard/utils/eliteSeatHelpers'
import { formatNumberWithCommas } from '@src/utils/functions'
import { useEliteSeatProgressUpdatedAllLevelsQuery } from './__generated__/useEliteSeatProgressUpdatedAllLevelsQuery.graphql'
import { t } from 'i18next'
import { toast } from 'react-toastify'

import type { User, UserEliteSeat } from '@src/stores/auth/user'
import { useAuthUser, getAuthUser, updateAuthUser } from '@src/stores/auth/user'

const INITIAL_LEVEL_NAME = 'Casual Visitor'
const REQUIRED_WAGER = 10000  // Required wager to reach level 1

const useEliteSeatProgressUpdated = () => {
    const dispatch = useDispatch()

    const user = useAuthUser()
    const [eliteProgress, setEliteProgress] = useState(() => {
        // Initialize state with data from the store
        if (user) return user.eliteSeat;
        return null;
    })

    const eliteSeatLevelsData = useLazyLoadQuery<useEliteSeatProgressUpdatedAllLevelsQuery>(
        eliteSeatLevelsQuery,
        {},
        { fetchPolicy: 'store-or-network' }
    );

    const wager = useMemo(() => (eliteProgress?.wagerAmountUsd ?? 0) / 100, [eliteProgress])
    const { wagerLeft, nextBonus } = getWagerLeftValueAndBonus(wager, eliteSeatLevelsData.eliteSealLevels);

    const eliteSeatInfo = useMemo(() => {
        if (!eliteProgress) return null

        const { currentLevel, nextLevel, currentTier, tierProgress } = eliteProgress
        const currentTierCorrect = currentTier === -1 ? 0 : currentTier
        const currentLevelName = currentLevel?.name ?? INITIAL_LEVEL_NAME
        const nextLevelName = nextLevel?.name ?? 'God'
        const progressbarTier = tierMapping[currentLevelName]

        const levelProgress = currentLevelName === INITIAL_LEVEL_NAME
            ? wager / REQUIRED_WAGER
            : currentTier + tierProgress

        const progressbarProgress = (currentLevelName === INITIAL_LEVEL_NAME ? levelProgress : tierProgress) * 100

        return {
            currentLevelName,
            nextLevelName,
            currentTierCorrect,
            tierProgress,
            progressbarProgress: progressbarProgress.toFixed(0),
            progressbarTier,
            levelProgress
        }
    }, [eliteProgress, wager])

    useEffect(() => {
        const subscription = requestSubscription<useEliteSeatProgressUpdatedSubscription>(Env, {
            subscription: eliteSeatProgressUpdatedSubscription,
            variables: {},
            onNext: (response) => {
                const currentUser = getAuthUser()

                if (response?.eliteSeatProgressUpdated) {
                    const updatedEliteSeat = response.eliteSeatProgressUpdated
                    const updatedUser = {
                        ...currentUser,
                        eliteSeat: updatedEliteSeat,
                    } as User

                    setEliteProgress(() => updatedUser.eliteSeat as UserEliteSeat)
                    updateAuthUser(updatedUser)

                    const currentTier = currentUser?.eliteSeat?.currentTier
                    const updatedTier = updatedEliteSeat?.currentTier

                    // show only when tier is updated
                    if (currentTier !== updatedTier) {
                        toast.success(t('profile.dashboard.levelReachedNotification', { tier: updatedTier }))
                    }
                }
            },
            onError: (error) => console.error('Error updating elite seat progress:', error),
        })

        return () => subscription.dispose()  // Cleanup on unmount
    }, [dispatch])

    return {
        setEliteProgress,
        eliteProgress,
        wager,
        wagerLeft: wagerLeft,
        nextBonus: formatNumberWithCommas(nextBonus),
        currentLevelName: eliteSeatInfo?.currentLevelName ?? 'Casual Visitor',
        currentTier: eliteSeatInfo?.currentTierCorrect ?? 0,
        tierProgress: eliteSeatInfo?.tierProgress ?? 0,
        progressbarProgress: eliteSeatInfo?.progressbarProgress ?? '0',
        levelProgress: eliteSeatInfo?.levelProgress ?? 0,
        progressbarTier: eliteSeatInfo?.progressbarTier ?? 0,
        nextLevelName: eliteSeatInfo?.nextLevelName ?? "God"
    }
}

export default useEliteSeatProgressUpdated

const eliteSeatProgressUpdatedSubscription = graphql`
  subscription useEliteSeatProgressUpdatedSubscription {
    eliteSeatProgressUpdated {
      currentLevel {
        name
        tierStartUsd
        tierStartLevel
        tierStep
        tierCount
        bonusUsd
      }
      nextLevel {
        name
        tierStartUsd
        tierStartLevel
        tierStep
        tierCount
        bonusUsd
      }
      currentTier
      tierProgress
      levelProgress
      pendingPayouts {
        levelNr
        bonusUsd
        dateTime
      }
      wagerAmountUsd
    }
  }
`

const tierMapping = {
    "Casual Visitor": 0,
    "Rising Star": 1,
    "Wager Dabbler": 2,
    "Bet Master": 3,
    "Pro Gambler": 4,
    "Casino Rockstar": 5,
    "High Roller": 6,
    "Prodigy Gambler": 7,
    "Bets Conqueror": 8,
    "Wager Phenom": 9,
    "Wager Titan": 10,
    "Risk Sensei": 11,
    "Payout Guru": 12,
    "Immaculate Gambler": 13
};

const eliteSeatLevelsQuery = graphql`
    query useEliteSeatProgressUpdatedAllLevelsQuery {
        eliteSealLevels {
        name
        tierStartUsd
        tierStartLevel
        tierStep
        tierCount
        bonusUsd
        }
    }
`;
